import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { CodeBlock } from '../CodeBlock';

export interface PanelMarkdownProps {
  props: { content: string };
}

export function PanelMarkdown({ props }: PanelMarkdownProps) {
  return (
    <div className="text-sd-text text-sd-sm leading-normal break-words min-w-0">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          a: ({ children, href }) => (
            <a href={href} className="text-sd-primary underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          h1: ({ children }) => <h1 className="text-sd-base font-semibold mt-3 mb-1">{children}</h1>,
          h2: ({ children }) => <h2 className="text-sd-base font-semibold mt-3 mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sd-sm font-semibold mt-2 mb-0.5">{children}</h3>,
          code: ({ children, className }) => {
            if (className) return <code className={className}>{children}</code>;
            return (
              <code className="bg-sd-surface-elevated rounded-sd-sm px-1.5 py-0.5 text-sd-sm font-mono">
                {children}
              </code>
            );
          },
          ul: ({ children }) => (
            <ul className="ml-4 space-y-1 list-disc marker:text-sd-text-muted/40">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="ml-4 space-y-1 list-decimal marker:text-sd-text-muted/40">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-sd-warning/40 pl-3 my-2 text-sd-text-secondary italic">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-sd-border/30" />,
          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
          table: ({ children }) => (
            <div className="overflow-x-auto my-2 rounded-sd-sm border border-sd-border/40">
              <table className="border-collapse w-full text-sd-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-sd-surface-elevated/80 border-b border-sd-border/40">{children}</thead>
          ),
          tr: ({ children, ...props }) => {
            const isHeader = (props as any).node?.parentNode?.tagName === 'thead';
            return (
              <tr
                className={
                  isHeader
                    ? ''
                    : 'even:bg-sd-surface-elevated/30 transition-colors hover:bg-sd-surface-elevated/50'
                }
              >
                {children}
              </tr>
            );
          },
          th: ({ children }) => (
            <th className="px-3 py-1.5 font-semibold text-left text-sd-text-secondary text-sd-xs uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-1.5 border-t border-sd-border/20">{children}</td>
          ),
        }}
      >
        {props.content}
      </Markdown>
    </div>
  );
}
